import { useMemo } from 'react';
import { FileWarning, ShieldCheck, Upload } from 'lucide-react';
import type { VaultDocument } from '@app/shared';
import type { AttentionItem, WebModule } from '../../app/types.ts';
import { familyRecords, scoreText } from '../../platform/search.ts';
import { daysUntil, dueLabelInline } from '../../platform/time.ts';
import { CATEGORY_LABELS, useDocuments } from './shared.ts';

function useExpiringDocuments(): AttentionItem[] {
  const docs = useDocuments();
  return useMemo(
    () =>
      (docs ?? [])
        .filter((d) => d.expiresOn && daysUntil(d.expiresOn) <= 30 && daysUntil(d.expiresOn) >= -60)
        .map((d) => ({
          key: `doc:${d.id}`,
          title: d.title,
          detail: `${CATEGORY_LABELS[d.category]} · ${daysUntil(d.expiresOn!) < 0 ? 'expired' : 'renews'} ${dueLabelInline(d.expiresOn!)}`,
          to: `/vault/doc/${d.id}`,
          tone: daysUntil(d.expiresOn!) < 0 ? ('danger' as const) : ('warning' as const),
          icon: FileWarning,
          days: daysUntil(d.expiresOn!),
        })),
    [docs],
  );
}

export const vaultModule: WebModule = {
  id: 'vault',
  label: 'Documents',
  icon: ShieldCheck,
  path: '/vault',
  nav: { group: 'household', order: 10 },
  routes: [
    { path: 'vault', lazy: async () => ({ Component: (await import('./VaultScreen.tsx')).VaultScreen }) },
    { path: 'vault/doc/:docId', lazy: async () => ({ Component: (await import('./DocumentScreen.tsx')).DocumentScreen }) },
  ],
  quickActions: [{ id: 'document', label: 'Document', icon: Upload, to: '/vault?add=1', adultOnly: true }],
  useAttention: useExpiringDocuments,
  async search(q, ctx) {
    const docs = await familyRecords<VaultDocument>('documents', ctx.familyId);
    return docs
      .map((d) => ({ d, score: scoreText(q, [d.title, d.tags.join(' '), d.fileName, d.receipt?.vendor, d.notes, CATEGORY_LABELS[d.category]]) }))
      .filter((x) => x.score > 0)
      .map(({ d, score }) => ({
        key: `documents:${d.id}`,
        title: d.title,
        subtitle: CATEGORY_LABELS[d.category],
        to: `/vault/doc/${d.id}`,
        group: 'Documents',
        icon: ShieldCheck,
        score,
      }));
  },
};
